import { useEffect, useState } from "react";
import { API } from "../api";

export default function QuestionManager() {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [difficulty, setDifficulty] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const [form, setForm] = useState({
    question_text: "",
    choice_a: "",
    choice_b: "",
    choice_c: "",
    choice_d: "",
    correct_answer: "A",
    difficulty: "medium",
  });

  // =========================
  // LOAD SUBJECT
  // =========================
  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    const res = await API.get("/subjects/");
    const data = res.data.results || res.data;
    setSubjects(data);
  };

  // =========================
  // LOAD QUESTIONS
  // =========================
  useEffect(() => {
    if (!selectedSubject) return;
    loadQuestions();
  }, [selectedSubject, page, pageSize, difficulty]);

  const loadQuestions = async () => {
    setLoading(true);

    const res = await API.get("/questions/", {
      params: {
        subject: selectedSubject,
        page,
        page_size: pageSize,
        difficulty: difficulty || undefined,
      },
    });

    const data = res.data.results || res.data;

    setQuestions(data);
    setTotalItems(res.data.count || data.length);
    setTotalPages(Math.ceil((res.data.count || data.length) / pageSize));

    setLoading(false);
  };

  // =========================
  // EDIT
  // =========================
  const handleEdit = (q) => {
    setEditingQuestion(q);

    setForm({
      question_text: q.question_text,
      choice_a: q.choices.find(c => c.key === "A")?.text || "",
      choice_b: q.choices.find(c => c.key === "B")?.text || "",
      choice_c: q.choices.find(c => c.key === "C")?.text || "",
      choice_d: q.choices.find(c => c.key === "D")?.text || "",
      correct_answer: q.correct_answer,
      difficulty: q.difficulty,
    });

    setShowModal(true);
  };

  // =========================
  // DELETE
  // =========================
  const handleDelete = async (id) => {
    if (!confirm("ยืนยันการลบข้อนี้?")) return;

    await API.delete(`/admin/questions/${id}/`);
    loadQuestions();
  };

  // =========================
  // SAVE
  // =========================
  const handleSave = async () => {
    await API.put(`/admin/questions/${editingQuestion.id}/`, form);
    setShowModal(false);
    loadQuestions();
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto">

        <h2 className="text-2xl font-bold mb-4">📋 Manage Questions</h2>

        {/* FILTER */}
        <div className="bg-white p-4 rounded-xl shadow flex gap-4">
          <select
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded"
          >
            <option value="">-- Select Subject --</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.question_count})
              </option>
            ))}
          </select>

          <select
            onChange={(e) => {
              setDifficulty(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border rounded"
          >
            <option value="">All Difficulty</option>
            <option value="easy">easy</option>
            <option value="medium">medium</option>
            <option value="hard">hard</option>
          </select>
        </div>

        {/* TABLE */}
        {selectedSubject && (
          <div className="bg-white mt-6 rounded-xl shadow overflow-hidden">

            {loading ? (
              <p className="p-6">⏳ Loading...</p>
            ) : (
              <div>

                <table className="w-full">
                  <thead className="bg-indigo-500 text-white">
                    <tr>
                      <th className="w-[5%] p-2">#</th>
                      <th className="w-[35%] text-left p-3">Question</th>
                      <th className="w-[45%] text-left p-3">Answer</th>
                      <th className="w-[15%] text-center">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {questions.map((q, index) => (
                      <tr key={q.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 text-center">
                          {(page - 1) * pageSize + index + 1}
                        </td>

                        <td className="p-2">{q.question_text}</td>

                        {/* ANSWER */}
                        <td className="p-3 align-top">
                          <div className="max-h-[150px] overflow-auto pr-1">
                            {[...q.choices]
                              .sort((a, b) => a.key.localeCompare(b.key)) // 🔥 FIX ไม่ให้สุ่ม
                              .map(choice => {
                                const isCorrect = choice.key === q.correct_answer;

                                return (
                                  <div
                                    key={choice.key}
                                    className={`
                                      px-3 py-2 rounded mb-2 text-sm border
                                      ${isCorrect
                                        ? "bg-green-200 text-green-800 font-bold border-green-500"
                                        : "bg-gray-50"
                                      }
                                    `}
                                  >
                                    {choice.key}. {choice.text}
                                    {isCorrect && " ✔"}
                                  </div>
                                );
                              })}
                          </div>
                        </td>

                        <td className="text-center space-x-2">
                          <button
                            onClick={() => handleEdit(q)}
                            className="text-blue-500 hover:text-blue-700"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(q.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* FOOTER */}
                <div className="p-4 flex justify-between items-center border-t bg-gray-50">

                  <div>
                    แสดง
                    <select
                      className="mx-2 border px-2 py-1 rounded"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {[20, 50, 100, 200, 300].map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    รายการ (ทั้งหมด {totalItems})
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                      className="px-2 py-1 border rounded disabled:opacity-50"
                    >
                      ◀
                    </button>

                    <span>{page} / {totalPages}</span>

                    <button
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                      className="px-2 py-1 border rounded disabled:opacity-50"
                    >
                      ▶
                    </button>
                  </div>

                </div>

              </div>
            )}
          </div>
        )}

        {/* MODAL */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center">
            <div className="bg-white p-6 rounded w-[500px]">

              <h2 className="font-bold mb-3">Edit Question</h2>

              <textarea
                className="w-full border p-2 mb-2"
                value={form.question_text}
                onChange={(e) =>
                  setForm({ ...form, question_text: e.target.value })
                }
              />

              {["A","B","C","D"].map(key => (
                <input
                  key={key}
                  className="w-full border p-2 mb-2"
                  value={form[`choice_${key.toLowerCase()}`]}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      [`choice_${key.toLowerCase()}`]: e.target.value
                    })
                  }
                />
              ))}

              <select
                value={form.correct_answer}
                onChange={(e) =>
                  setForm({ ...form, correct_answer: e.target.value })
                }
                className="border p-2 mb-2"
              >
                {["A","B","C","D"].map(c => <option key={c}>{c}</option>)}
              </select>

              <div className="flex justify-end gap-2">
                <button onClick={() => setShowModal(false)}>Cancel</button>
                <button onClick={handleSave} className="bg-blue-500 text-white px-3 py-1">
                  Save
                </button>
              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
}